import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LucideIcon } from 'lucide-react';

export interface MenuItem {
  label: string;
  onSelect: () => void;
  icon?: LucideIcon;
  danger?: boolean;
  disabled?: boolean;
}

const MENU_WIDTH = 200;
const GAP = 6;

/**
 * Small popover menu, e.g. for row actions behind a ⋯ button.
 * Rendered in a portal with fixed positioning so cards and scrolling tables never clip it.
 */
export const Menu: React.FC<{
  /** Render the trigger; spread `props` onto a button */
  trigger: (props: { onClick: (event?: React.MouseEvent) => void; 'aria-expanded': boolean; 'aria-haspopup': 'menu' }) => React.ReactNode;
  items: MenuItem[];
  align?: 'left' | 'right';
  className?: string;
}> = ({ trigger, items, align = 'right', className = '' }) => {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const place = useCallback(() => {
    const anchor = anchorRef.current?.getBoundingClientRect();
    if (!anchor) return;
    const menuHeight = menuRef.current?.offsetHeight ?? items.length * 36 + 12;
    const menuWidth = menuRef.current?.offsetWidth ?? MENU_WIDTH;
    const fitsBelow = anchor.bottom + GAP + menuHeight <= window.innerHeight - 8;
    const top = fitsBelow ? anchor.bottom + GAP : Math.max(8, anchor.top - GAP - menuHeight);
    const rawLeft = align === 'right' ? anchor.right - menuWidth : anchor.left;
    const left = Math.min(Math.max(8, rawLeft), window.innerWidth - menuWidth - 8);
    setPosition({ top, left });
  }, [align, items.length]);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!anchorRef.current?.contains(target) && !menuRef.current?.contains(target)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
    const onViewportChange = () => setOpen(false);
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onViewportChange);
    window.addEventListener('scroll', onViewportChange, true);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onViewportChange);
      window.removeEventListener('scroll', onViewportChange, true);
    };
  }, [open]);

  return (
    <div ref={anchorRef} className={`relative inline-block ${className}`}>
      {trigger({
        onClick: (event) => {
          // Row actions often sit inside clickable rows
          event?.stopPropagation();
          setOpen((v) => !v);
        },
        'aria-expanded': open,
        'aria-haspopup': 'menu',
      })}
      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{ top: position?.top ?? -9999, left: position?.left ?? -9999, minWidth: MENU_WIDTH }}
            className="fixed z-[70] animate-fade-up rounded-2xl bg-white p-1.5 font-ui shadow-[0_16px_40px_-12px_rgba(0,0,0,0.2)] ring-1 ring-black/[0.06]"
            onMouseDown={(event) => event.stopPropagation()}
          >
            {items.map(({ label, onSelect, icon: Icon, danger, disabled }) => (
              <button
                key={label}
                type="button"
                role="menuitem"
                disabled={disabled}
                onClick={(event) => {
                  event.stopPropagation();
                  setOpen(false);
                  onSelect();
                }}
                className={`flex h-9 w-full items-center gap-2.5 whitespace-nowrap rounded-xl px-3 text-left text-[13px] transition-colors hover:bg-neutral-100 focus-visible:bg-neutral-100 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-40 ${
                  danger ? 'text-primary-700' : 'text-ink'
                }`}
              >
                {Icon && <Icon className={`h-4 w-4 ${danger ? 'text-primary-600' : 'text-neutral-400'}`} strokeWidth={1.75} />}
                {label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
};
